import React, { useState } from 'react';
import { Table as FlowbiteTable } from 'flowbite-react';
import {
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconSortAscending,
  IconSortDescending
} from '@tabler/icons-react';
import CardBox from './CardBox';

/**
 * Table component - Data tables with MatDash styling
 * Extends Flowbite Table with sorting, selection, and responsive design
 */
const Table = ({
  columns = [],
  data = [],
  onSort = null,
  sortColumn = null,
  sortDirection = 'asc',
  selectable = false,
  selectedRows = [],
  onSelectRow = null,
  onSelectAll = null,
  hoverable = true,
  striped = false,
  compact = false,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  cardWrapper = true,
  title = '',
  actions = null,
}) => {
  const [localSelectedRows, setLocalSelectedRows] = useState([]);
  const effectiveSelectedRows = selectable ? (selectedRows || localSelectedRows) : [];

  const handleSelectRow = (rowId) => {
    if (onSelectRow) {
      onSelectRow(rowId);
    } else {
      setLocalSelectedRows(prev => {
        if (prev.includes(rowId)) {
          return prev.filter(id => id !== rowId);
        }
        return [...prev, rowId];
      });
    }
  };

  const handleSelectAll = () => {
    const allRowIds = data.map((row, index) => row.id || index);
    const allSelected = effectiveSelectedRows.length === data.length;

    if (onSelectAll) {
      onSelectAll(!allSelected);
    } else {
      setLocalSelectedRows(allSelected ? [] : allRowIds);
    }
  };

  const getSortIcon = (column) => {
    if (!onSort) return null;

    if (sortColumn !== column.key) {
      return <IconSelector size={16} className="text-gray-400" />;
    }

    return sortDirection === 'asc'
      ? <IconSortAscending size={16} className="text-primary" />
      : <IconSortDescending size={16} className="text-primary" />;
  };

  const tableContent = (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            {selectable && (
              <th className="px-6 py-3">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded
                           focus:ring-primary focus:ring-2 dark:bg-gray-700
                           dark:border-gray-600"
                  checked={data.length > 0 && effectiveSelectedRows.length === data.length}
                  onChange={handleSelectAll}
                />
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key}
                className={`
                  px-6 ${compact ? 'py-2' : 'py-3'}
                  ${column.sortable && onSort ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''}
                  ${column.align === 'right' ? 'text-right' : ''}
                  ${column.align === 'center' ? 'text-center' : ''}
                `}
                onClick={() => column.sortable && onSort && onSort(column.key)}
              >
                <div className="flex items-center gap-2">
                  <span>{column.label}</span>
                  {column.sortable && getSortIcon(column)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-6 py-8 text-center">
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-6 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => {
              const rowId = row.id || rowIndex;
              const isSelected = effectiveSelectedRows.includes(rowId);

              return (
                <tr
                  key={rowId}
                  className={`
                    bg-white border-b dark:bg-gray-800 dark:border-gray-700
                    ${hoverable ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : ''}
                    ${striped && rowIndex % 2 === 1 ? 'bg-gray-50 dark:bg-gray-900' : ''}
                    ${isSelected ? 'bg-primary/10 dark:bg-primary/20' : ''}
                  `}
                >
                  {selectable && (
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded
                                 focus:ring-primary focus:ring-2 dark:bg-gray-700
                                 dark:border-gray-600"
                        checked={isSelected}
                        onChange={() => handleSelectRow(rowId)}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`
                        px-6 ${compact ? 'py-2' : 'py-4'}
                        ${column.align === 'right' ? 'text-right' : ''}
                        ${column.align === 'center' ? 'text-center' : ''}
                        ${column.className || ''}
                      `}
                    >
                      {column.render
                        ? column.render(row[column.key], row, rowIndex)
                        : row[column.key]
                      }
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  if (cardWrapper) {
    return (
      <CardBox title={title} headerAction={actions}>
        {tableContent}
      </CardBox>
    );
  }

  return tableContent;
};

// Responsive Table Variant
export const ResponsiveTable = ({ columns, data, ...props }) => {
  return (
    <>
      <div className="block lg:hidden">
        {/* Mobile card view */}
        {data.map((row, index) => (
          <CardBox key={index} className="mb-4">
            {columns.map((column) => (
              <div key={column.key} className="flex justify-between py-2 border-b last:border-0">
                <span className="font-medium text-gray-600">{column.label}:</span>
                <span className="text-gray-900">
                  {column.render
                    ? column.render(row[column.key], row, index)
                    : row[column.key]
                  }
                </span>
              </div>
            ))}
          </CardBox>
        ))}
      </div>

      <div className="hidden lg:block">
        {/* Desktop table view */}
        <Table columns={columns} data={data} {...props} />
      </div>
    </>
  );
};

// Data Table with pagination
export const DataTable = ({
  columns,
  data,
  pageSize = 10,
  currentPage = 1,
  totalItems = 0,
  onPageChange = null,
  ...props
}) => {
  const totalPages = Math.ceil((totalItems || data.length) / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems || data.length);

  return (
    <>
      <Table columns={columns} data={data} {...props} />

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 border-t">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {startItem} to {endItem} of {totalItems || data.length} results
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange && onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm rounded-md bg-gray-100 hover:bg-gray-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              Previous
            </button>

            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange && onPageChange(pageNum)}
                    className={`
                      px-3 py-1 text-sm rounded-md
                      ${currentPage === pageNum
                        ? 'bg-primary text-gray-900'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onPageChange && onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm rounded-md bg-gray-100 hover:bg-gray-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Table;